import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'area';
  title?: string;
  data: any[];
  xKey?: string;
  yKey?: string | string[];
  colors?: string[];
  labelKey?: string;
  valueKey?: string;
}

interface ChartRendererProps {
  chartData: ChartData;
}

/* OmniMind categorical palette - same hues as the design tokens */
const COLORS = [
  '#2354F0', // blue
  '#12B981', // green
  '#6D5AE6', // purple
  '#D9910E', // amber
  '#0E9B8E', // teal
  '#C2418C', // pink
  '#CE4630', // red
  '#D97757', // clay
];

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData }) => {
  const { type, title, data, xKey, yKey, colors = COLORS, labelKey, valueKey } = chartData;

  if (!data || data.length === 0) {
    return (
      <div className="note">No data available to display this chart</div>
    );
  }

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,18,22,.08)" />
              <XAxis dataKey={xKey || 'name'} tick={{ fill: '#69747A', fontSize: 11 }} />
              <YAxis tick={{ fill: '#69747A', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(14,18,22,.09)',
                  borderRadius: '11px',
                  boxShadow: '0 3px 14px rgba(18,24,34,.055)',
                  fontSize: '11.5px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#69747A' }} />
              {Array.isArray(yKey) ? (
                yKey.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={colors[index % colors.length]}
                    radius={[4, 4, 0, 0]}
                  />
                ))
              ) : (
                <Bar
                  dataKey={yKey || 'value'}
                  fill={colors[0]}
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,18,22,.08)" />
              <XAxis dataKey={xKey || 'name'} tick={{ fill: '#69747A', fontSize: 11 }} />
              <YAxis tick={{ fill: '#69747A', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(14,18,22,.09)',
                  borderRadius: '11px',
                  boxShadow: '0 3px 14px rgba(18,24,34,.055)',
                  fontSize: '11.5px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#69747A' }} />
              {Array.isArray(yKey) ? (
                yKey.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey={yKey || 'value'}
                  stroke={colors[0]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,18,22,.08)" />
              <XAxis dataKey={xKey || 'name'} tick={{ fill: '#69747A', fontSize: 11 }} />
              <YAxis tick={{ fill: '#69747A', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(14,18,22,.09)',
                  borderRadius: '11px',
                  boxShadow: '0 3px 14px rgba(18,24,34,.055)',
                  fontSize: '11.5px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#69747A' }} />
              {Array.isArray(yKey) ? (
                yKey.map((key, index) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    fill={colors[index % colors.length]}
                    fillOpacity={0.6}
                  />
                ))
              ) : (
                <Area
                  type="monotone"
                  dataKey={yKey || 'value'}
                  stroke={colors[0]}
                  fill={colors[0]}
                  fillOpacity={0.6}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey={valueKey || 'value'}
                nameKey={labelKey || 'name'}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(14,18,22,.09)',
                  borderRadius: '11px',
                  boxShadow: '0 3px 14px rgba(18,24,34,.055)',
                  fontSize: '11.5px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="card" style={{ margin: '12px 0' }}>
      <div className="card-hd">
        <h4>{title || 'Chart'}</h4>
      </div>
      <div className="card-b">{renderChart()}</div>
    </div>
  );
};
